import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  InputNumber,
  Radio,
  Result,
  Space,
  Switch,
  Tag,
  Typography,
  notification,
} from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import ModuleNav, { MODULE_NAVS } from '../../components/ModuleNav';
import DashboardSection from '../dashboard/components/DashboardSection';

const { Text, Paragraph, Title } = Typography;

/**
 * Runtime switches for the encounter/claims workflow overhaul
 * (claims_rollout_settings, see api_v1/docs/CLAIMS_ROLLOUT_SWITCHES.md).
 *
 * The new flow runs in parallel with the old one. Nothing here changes the
 * existing checkout or claims screens; each switch turns on one piece of the
 * new flow, and every facility-scoped switch acts only on facilities in the
 * pilot list.
 *
 * admin.* | admin.system_config — the same gate the API applies.
 */

// Listed in the certification order: read-mostly first, the unattended writer last.
const FLAGS = [
  {
    key: 'preview_enabled',
    label: 'Release-candidate route (/test)',
    scope: 'global',
    help: 'Opens the new facility flow at /test for facility admins at pilot facilities and Aman staff. The live facility pages are unaffected.',
  },
  {
    key: 'item_removal_enabled',
    label: 'Remove claimable item',
    scope: 'facility',
    help: 'Facilities can remove an approved item with a reason (service not rendered / client refused care). The item stays on the claim for audit.',
  },
  {
    key: 'pending_banner_enabled',
    label: 'Pending claims banner',
    scope: 'facility',
    help: 'Reminder for every facility user while claims from previous months are unsubmitted.',
  },
  {
    key: 'submit_separation_enabled',
    label: 'Submit Claim (separate from checkout)',
    scope: 'facility',
    help: 'Submit Claim and batch submission in the new flow; admin Encounters show Submit Claim / Submit All, the Pending submission page and latest-submission sorting.',
  },
  {
    key: 'month_complete_notice_enabled',
    label: 'Monthly completion notice',
    scope: 'facility',
    help: 'Claims-team notification once a facility has resolved every claimable item for an encounter month. Also turns on the admin notification bell.',
  },
  {
    key: 'auto_checkout_enabled',
    label: 'Automatic checkout after check-in',
    scope: 'facility',
    help: 'Hourly job closes non-inpatient encounters after the interval below, WITHOUT submitting a claim. Enable last — it is the only unattended writer.',
  },
];

const parseIds = (text) =>
  String(text || '')
    .split(/[\s,]+/)
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n > 0);

const ClaimsRolloutPage = () => {
  const { refreshProfile } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const allowed = useMemo(
    () => hasAnyPermission(['admin.*', 'admin.system_config']),
    [hasAnyPermission],
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null); // settings as stored
  const [draft, setDraft] = useState(null);
  const [pilotMode, setPilotMode] = useState('list'); // 'all' | 'list'
  const [pilotText, setPilotText] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getClaimsRollout();
      if (resp?.data?.error) return;
      const settings = resp?.data?.data?.settings || null;
      setSaved(settings);
      setDraft(settings);
      const ids = settings?.pilot_facility_ids;
      setPilotMode(ids === null ? 'all' : 'list');
      setPilotText(Array.isArray(ids) ? ids.join(', ') : '');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const changes = useMemo(() => {
    if (!saved || !draft) return {};
    const out = {};
    FLAGS.forEach(({ key }) => {
      if (Boolean(draft[key]) !== Boolean(saved[key])) out[key] = Boolean(draft[key]);
    });
    if (Number(draft.auto_checkout_hours) !== Number(saved.auto_checkout_hours)) {
      out.auto_checkout_hours = Number(draft.auto_checkout_hours);
    }
    const nextIds = pilotMode === 'all' ? null : parseIds(pilotText);
    const prevIds = saved.pilot_facility_ids;
    const same =
      nextIds === null || prevIds === null
        ? nextIds === prevIds
        : JSON.stringify([...nextIds].sort()) === JSON.stringify([...(prevIds || [])].sort());
    if (!same) out.pilot_facility_ids = nextIds;
    return out;
  }, [saved, draft, pilotMode, pilotText]);

  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const resp = await lib.updateClaimsRollout({ ...changes, reason: reason || undefined });
      if (resp?.data?.error) {
        notification.error({ message: resp.data.message || 'Failed to update switches' });
        return;
      }
      notification.success({
        message: 'Rollout switches saved',
        description: 'Facility users pick up the change on their next profile refresh.',
      });
      setReason('');
      await load();
      // Your own portal reads these flags from your profile.
      refreshProfile?.();
    } finally {
      setSaving(false);
    }
  };

  if (!allowed) {
    return (
      <Result
        status="403"
        title="Admin only"
        subTitle="Changing the claims rollout switches requires admin.* or admin.system_config."
      />
    );
  }

  const pilotIds = parseIds(pilotText);

  return (
    <div style={{ padding: 16, maxWidth: 820, margin: '0 auto' }}>
      <ModuleNav items={MODULE_NAVS.claims} />
      <Title level={3} style={{ marginTop: 0 }}>Claims rollout switches</Title>
      <Paragraph type="secondary">
        The new encounter &amp; claims flow runs alongside the current one. Each switch turns on
        one part of it; facility switches act only on the pilot facilities below. Turning a
        switch off returns those facilities to the current flow immediately.
      </Paragraph>

      {saved && saved.schema_ready === false ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Not migrated on this server"
          description="Run sqls/migrations/20261007_claims_rollout_settings.sql. Until then every switch reads as off."
        />
      ) : null}

      <DashboardSection
        id="claims-rollout-pilot"
        title="Pilot facilities"
        subtitle={
          saved?.updated_at
            ? `Last changed ${saved.updated_at}${saved.updated_by ? ` by staff #${saved.updated_by}` : ''}`
            : undefined
        }
        collapsible={false}
      >
        <Radio.Group value={pilotMode} onChange={(e) => setPilotMode(e.target.value)}>
          <Space direction="vertical">
            <Radio value="list">Only these facilities</Radio>
            <Radio value="all">All facilities</Radio>
          </Space>
        </Radio.Group>
        {pilotMode === 'list' ? (
          <div style={{ marginTop: 12 }}>
            <Input.TextArea
              rows={2}
              placeholder="Facility IDs, comma separated — e.g. 12, 15"
              value={pilotText}
              onChange={(e) => setPilotText(e.target.value)}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {pilotIds.length
                ? `${pilotIds.length} facilit${pilotIds.length === 1 ? 'y' : 'ies'}`
                : 'Empty — facility switches act on nobody. Aman staff can still review /test.'}
            </Text>
          </div>
        ) : (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message="Every facility gets every switch that is on"
          />
        )}
      </DashboardSection>

      <DashboardSection id="claims-rollout-flags" title="Switches" collapsible={false}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          {FLAGS.map((flag) => (
            <div key={flag.key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <Switch
                checked={Boolean(draft?.[flag.key])}
                disabled={!draft || loading}
                onChange={(v) => setDraft((d) => ({ ...d, [flag.key]: v }))}
              />
              <div style={{ minWidth: 0 }}>
                <Space size={6} wrap>
                  <Text strong>{flag.label}</Text>
                  <Tag>{flag.scope === 'global' ? 'All reviewers' : 'Pilot facilities'}</Tag>
                </Space>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>{flag.help}</Text>
                </div>
                {flag.key === 'auto_checkout_enabled' ? (
                  <Space style={{ marginTop: 8 }}>
                    <Text>Close after</Text>
                    <InputNumber
                      min={1}
                      max={720}
                      value={draft?.auto_checkout_hours ?? 24}
                      onChange={(v) => setDraft((d) => ({ ...d, auto_checkout_hours: v || 24 }))}
                    />
                    <Text>hours (Inpatient is never closed)</Text>
                  </Space>
                ) : null}
              </div>
            </div>
          ))}
        </Space>
      </DashboardSection>

      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
        <Input
          placeholder="Reason for this change (kept in the activity log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={!dirty}
        />
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Reload
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={save} loading={saving} disabled={!dirty}>
            Save
          </Button>
        </Space>
      </Space>
    </div>
  );
};

export default ClaimsRolloutPage;
